import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Search from 'classes/Recipe/Search/Search';
import Picture from 'components/desktop/Accessory/Search/Components/SearchResultListRow/Picture';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import IconComponent from 'components/desktop/_general/Icon/IconComponent';
import IconCo2Round from 'components/desktop/_general/IconCo2/IconCo2Round';
import { SearchLogic } from 'enums/searchLogic';
import { usePermissions } from 'hooks/usePermissions';
import { IExclusionLogicSelectItem } from 'interfaces/IExclusionLogicSelectItem';
import * as Accessory from 'types/Accessory/Search/Accessory';
import * as Icon from 'types/Icon/Icon';

interface IProps {
	handleArchiveAccessory: (input: string) => Promise<void>;
	accessory: Accessory.Type;
}

const SearchResultListRow = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isRecipeReader } = usePermissions();
	const { isAccessoryEditor } = usePermissions();

	const onRecipeLinkClick = async (id: string, name: string, e: any) => {
		e.preventDefault();
		const recipeAdvancedSearch = new Search();
		recipeAdvancedSearch.selectIngredient.saved = [
			{ id: id, name: name, logic: SearchLogic.Include } as IExclusionLogicSelectItem,
		];
		const url: string | null = recipeAdvancedSearch.mapToUrl();
		if (url) navigate(`/recipe/search/ingredient/${url}`);
	};

	const handleAccessoryDetailClick = (): void => {
		navigate(`/accessory/detail/${props.accessory.id}`);
	};

	const getButtonDataEdit = (): BtnData | undefined => {
		if (isAccessoryEditor) {
			return {
				onClick: handleAccessoryDetailClick,
			};
		}
	};

	const getButtonDataArchive = (): BtnData | undefined => {
		if (isAccessoryEditor) {
			return {
				onClick: () => props.handleArchiveAccessory(props.accessory.id),
			};
		}
	};

	const getButtonDataView = (): BtnData | undefined => {
		if (!isAccessoryEditor) {
			return {
				onClick: handleAccessoryDetailClick,
			};
		}
	};

	const renderRecipeLink = (): JSX.Element => {
		if (props.accessory.countOfRecipes > 0) {
			if (isRecipeReader) {
				return (
					<span
						className="primary"
						onClick={(e: any) =>
							onRecipeLinkClick(props.accessory.id, props.accessory.name, e)
						}
						style={{ zIndex: 100 }}
					>
						{`${props.accessory.countOfRecipes} ${t('_general:RECIPE', {
							count: props.accessory.countOfRecipes,
						})}`}
					</span>
				);
			} else {
				return (
					<span className="primary">
						{`${props.accessory.countOfRecipes} ${t('_general:RECIPE', {
							count: props.accessory.countOfRecipes,
						})}`}
					</span>
				);
			}
		}
		return <>{`0 ${t('_general:RECIPES', { count: props.accessory.countOfRecipes })}`}</>;
	};

	const renderButtons = (): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit()}
				archive={getButtonDataArchive()}
				view={getButtonDataView()}
			/>
		);
	};

	return (
		<Link to={`/accessory/detail/${props.accessory.id}`} className="unstyled-link">
			<div className="recipe-table-item">
				<Picture image={props.accessory.image} />
				<div id="column-first">
					<div id="title">
						<strong>{props.accessory.name}</strong>
					</div>
					<div id="info">{renderRecipeLink()}</div>
				</div>
				<div id="column-second">
					<div className="row">
						<div className="col-5">
							<span className="grey">{t('_general:LAST_CHANGE_DATE')}:</span>
							<br />
							<span className="grey">{t('_general:LAST_EDITOR')}:</span>
							<br />
						</div>
						<div className="col-7">
							<FormatedDate date={props.accessory.lastEditedDateUtc} />
							<br />
							<span>{Accessory.getAuthor(props.accessory)}</span>
						</div>
					</div>
				</div>
				<div id="column-last">
					<div id="icon-rows">
						{Accessory.getIcons(props.accessory)
							.slice(0, 4)
							.map((icon: Icon.Type, i: number) => (
								<IconComponent icon={icon} key={i} />
							))}
						<IconCo2Round
							color={
								props.accessory.carbonDioxideLabel
									? props.accessory.carbonDioxideLabel.color!
									: ''
							}
							label={
								props.accessory.carbonDioxideLabel
									? props.accessory.carbonDioxideLabel.label!
									: ''
							}
						/>
					</div>
					<div>{renderButtons()}</div>
				</div>
			</div>
		</Link>
	);
};

export default SearchResultListRow;
